import { useMutation } from '@apollo/client';
import Cookie from 'js-cookie';

import { getAdId } from '../helpers';
import { TrackAdClickByIntIdDocument } from '../track-ad-click-by-int-id-mutation.gql-types';
import { TrackAdImpressionByIntIdDocument } from '../track-ad-impression-by-int-id-mutation.gql-types';

export type AdsTrackingType = {
  isLoggedOut?: boolean;
  sid: string;
  trackingToken: string;
  onImpressionCallback?: () => void;
};

export type AdsTrackingReturnType = {
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleImpression: (isIntersecting: boolean) => void;
};

type AdsTrackMutationType = (
  id: number,
  trackingToken: string,
  scrambledId?: string // only if isLoggedOut
) => void;

export const useAdsTracking = ({
  isLoggedOut,
  sid,
  trackingToken,
  onImpressionCallback,
}: AdsTrackingType): AdsTrackingReturnType => {
  const [trackAdClickByIntId, { error: trackAdClickError }] = useMutation(
    TrackAdClickByIntIdDocument
  );
  const [trackAdImpressionByIntId, { error: trackAdImpressionError }] =
    useMutation(TrackAdImpressionByIntIdDocument);

  const trackClick: AdsTrackMutationType = (id, trackingToken, scrambledId) => {
    if (trackAdClickError) {
      throw new Error(trackAdClickError.message);
    }

    trackAdClickByIntId({
      variables: {
        input: {
          id,
          token: trackingToken,
          scrambledId,
        },
      },
    });
  };

  const trackImpression: AdsTrackMutationType = (
    id,
    trackingToken,
    scrambledId
  ) => {
    if (trackAdImpressionError) {
      throw new Error(trackAdImpressionError.message);
    }

    trackAdImpressionByIntId({
      variables: {
        input: {
          id,
          token: trackingToken,
          scrambledId,
        },
      },
    });
  };

  const scrambledId = isLoggedOut ? Cookie.get('s_id2') : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    const isVideoElement = event.target.toString().includes('Video');

    const cssClass =
      event.target instanceof HTMLElement ? event.target.className : '';

    if (!isVideoElement && !cssClass.includes('vjs')) {
      trackClick(getAdId(sid), trackingToken, scrambledId);
    }
  };

  const handleImpression = (isIntersecting: boolean): void => {
    if (isIntersecting) {
      trackImpression(getAdId(sid), trackingToken, scrambledId);
      onImpressionCallback?.();
    }
  };

  return {
    handleClick,
    handleImpression,
  };
};
